<script setup>
import { ref } from 'vue';
import { usePage } from '@inertiajs/inertia-vue3';
import AppLayout from "@/Layouts/AppLayout.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import DialogModal from "@/Components/DialogModal.vue";
import InputLabel from "@/Components/InputLabel.vue";
import TextInput from "@/Components/TextInput.vue";
import { Inertia } from "@inertiajs/inertia";
import PaginationLinks from "@/Components/PaginationLinks.vue";

const props = defineProps({
  vendors: Object,
  pageSearchQuery: String,
});

const searchQuery = ref(props.pageSearchQuery);
const currentUser = usePage().props.value.auth.user;

const activating = ref(null);
const activateError = ref('');

const startActivating = (vendor) => {
  activating.value = vendor;
};

const closeActivateErrorModal = () => {
  activating.value = null;
  activateError.value = '';
};

const activate = (vendor) => {
  Inertia.post(
    route('admin.vendors.activate', { vendor: vendor.vendorID }),
    {},
    {
      preserveScroll: true,
      onError: (error) => {
        activateError.value = error?.vendor_id || 'Activation failed.';
      },
    }
  );
  activating.value = null;
};

const querySearchRef = ref(null);
const makeSearchRequest = _.debounce(() => {
  const queryParameters = route().params || {};
  if (queryParameters.page) {
    delete queryParameters.page;
  }
  queryParameters['query'] = searchQuery.value || '';
  Inertia.get(route(route().current(), queryParameters), {}, { preserveScroll: true, preserveState: true });
}, 400);
</script>

<template>
  <AppLayout title="Deactivated Vendors">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        Deactivated Vendors
      </h2>
    </template>

    <div>
      <div class="mx-auto py-10 sm:px-6 lg:px-8">
        <div class="mt-5 mb-2 grid gap-2 lg:grid-cols-2 grid-cols-1">
          <div>
            <InputLabel for="search" value="Search" class="inline mr-2" />
            <TextInput
              id="search"
              v-model="searchQuery"
              type="text"
              class="mt-1 inline"
              placeholder="John Doe"
              ref="querySearchRef"
              @keyup="makeSearchRequest"
            />
          </div>
          <div class="lg:text-right">
            <a :href="route('admin.vendors.active')">
              <SecondaryButton>View Activated Vendors</SecondaryButton>
            </a>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full w-full text-left text-sm font-light">
            <thead class="border-b font-medium dark:border-neutral-500">
              <tr>
                <th scope="col" class="px-4 py-2">Name</th>
                <th scope="col" class="px-4 py-2">Code</th>
                <th scope="col" class="px-4 py-2">Account Number</th>
                <th scope="col" class="px-4 py-2">Notes</th>
                <th scope="col" class="px-4 py-2">Website</th>
                <th scope="col" class="px-4 py-2">Username</th>
                <th scope="col" class="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                class="border-b transition duration-300 ease-in-out hover:bg-neutral-200"
                v-for="vendor in vendors.data"
                :key="vendor.vendorID"
              >
                <td class="px-4 py-2 break-words" v-text="vendor.vendorName"></td>
                <td class="px-4 py-2 break-words" v-text="vendor.vendor_code"></td>
                <td class="px-4 py-2 break-words" v-text="vendor.accountNumber"></td>
                <td class="px-4 py-2 break-words" v-text="vendor.notes"></td>
                <td class="px-4 py-2 break-words">
                  <span v-if="vendor.www">
                    <a :href="`//${vendor.www}`" v-text="vendor.www" target="_blank"></a>
                  </span>
                </td>
                <td class="px-4 py-2 break-words" v-text="vendor.username"></td>
                <td>
                  <a
                    :href="route('admin.vendors.edit', { vendor: vendor.vendorID })"
                    class="fa fa-pen mr-1 p-1"
                    :title="`Edit ${vendor.vendorName}`"
                  ></a>

                  <button
                    class="fa cursor-pointer transition-colors fa fa-toggle-off text-gray-500 hover:text-gray-700"
                    :title="`Activate ${vendor.vendorName}`"
                    :disabled="!!activating"
                    @click="startActivating(vendor)"
                  ></button>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="text-center mt-5">
            <PaginationLinks :pagination="vendors" />
          </div>
        </div>
      </div>
    </div>

    <DialogModal :show="activating !== null" @close="closeActivateErrorModal">
      <template #title>
        <b>Please confirm</b>
      </template>

      <template #content>
        <div v-if="activateError" class="text-red-600 mb-2">
          {{ activateError }}
        </div>
        You are about to activate <b v-text="activating?.vendorName"></b>.
        <br />Are you sure?
      </template>

      <template #footer>
        <SecondaryButton @click="closeActivateErrorModal" class="mr-2">
          Cancel
        </SecondaryButton>
        <PrimaryButton @click="activate(activating)">
          Confirm
        </PrimaryButton>
      </template>
    </DialogModal>
  </AppLayout>
</template>